//Global imports
import { CButton, CCol, CFormTextarea, CRow } from '@coreui/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDispatch } from 'react-redux';
import { removeImageInFaq, updateFaqIdWithNote } from '../FAQs/Redux/faqsSlice';
import { appDispatch, RootState } from '../../../../app/store';
import { UPLOAD_IMAGE_ALLOWED_FILE_EXTENSIONS } from '../../../../Constant';
import { Language } from '../../../../language/English';
import { useSelector } from 'react-redux';

//Component imports
import DragHandle from '../DragHandle';

const FAQItem = ({
    id,
    content,
    onTextChange,
    onRemoveFaq,
    emptyLastFaq,
    lastIndex,
    length,
    onImageChange,
}: any) => {
    const dispatch = useDispatch<appDispatch>();
    const { setNodeRef, transform, transition } = useSortable({ id });
    const faqIdWithNote = useSelector(
        (state: RootState) => state.faqsReducer.faqIdWithNote,
    );

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        padding: '10px',
        width: '100%',
        marginLeft: '0px',
        marginBottom: '8px',
        border: '1px solid #ccc',
        borderRadius: '6px',
        backgroundColor: '#f7f5f0',
    };

    const getImageUrl = (file: File | string) => {
        if (!file) return null;
        if (typeof file === 'string') return file;
        return URL.createObjectURL(file);
    };

    const validateImage = async (
        e: React.ChangeEvent<HTMLInputElement>,
    ): Promise<void> => {
        if (!e.target.files || e.target.files.length === 0) return;

        const file = e.target.files[0];
        const fileExtension = file.name.split('.').pop()?.toLowerCase();

        if (
            !fileExtension ||
            !UPLOAD_IMAGE_ALLOWED_FILE_EXTENSIONS.includes(fileExtension)
        ) {
            alert(Language.UPLOAD_IMAGE_FORMAT_VALIDATION);
            return;
        }

        onImageChange && onImageChange(id, e.target.files?.[0] || null);

        e.target.value = '';
    };

    return (
        <CRow key={id} className="mb-3" ref={setNodeRef} style={style}>
            <CCol xs="auto" className="d-flex pt-2">
                <div className="d-flex flex-column">
                    <div className="align-self-start">
                        <DragHandle id={id} />
                    </div>
                </div>
            </CCol>

            <CCol>
                <div className="d-flex flex-column gap-2">
                    <CFormTextarea
                        placeholder={Language.QUESTION}
                        value={content.question}
                        style={{ height: '100px' }}
                        onChange={(e) =>
                            onTextChange(
                                id,
                                Language.QUESTION.toLocaleLowerCase(),
                                e.target.value,
                            )
                        }
                    />

                    <CFormTextarea
                        placeholder={Language.ANSWER}
                        value={content.answer}
                        style={{ height: '100px' }}
                        onChange={(e) =>
                            onTextChange(
                                id,
                                Language.ANSWER.toLocaleLowerCase(),
                                e.target.value,
                            )
                        }
                    />

                    {/* Image preview and upload */}
                    {content.image ? (
                        <div
                            className="position-relative d-inline-block"
                            style={{ width: '240px', height: '120px' }}
                        >
                            <img
                                src={content.image}
                                alt="FAQ"
                                className="img-thumbnail rounded"
                                style={{
                                    width: '100%',
                                    height: '120px',
                                    objectFit: 'cover',
                                    display: 'block',
                                }}
                            />
                            <CButton
                                type="button"
                                color="danger"
                                size="sm"
                                variant="ghost"
                                className="position-absolute top-0 end-0 m-2 p-0 d-flex align-items-center justify-content-center"
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '50%',
                                    zIndex: 2,
                                    backgroundColor: 'black',
                                }}
                                aria-label="Remove image"
                                onClick={() => {
                                    dispatch(removeImageInFaq({ id }));
                                    dispatch(updateFaqIdWithNote(id));
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: '18px',
                                        lineHeight: '1',
                                        color: 'white',
                                    }}
                                >
                                    &#10005;
                                </span>
                            </CButton>
                        </div>
                    ) : (
                        <div>
                            <label
                                className="btn btn-outline-primary btn-sm w-100"
                                style={{ maxWidth: '120px' }}
                            >
                                {Language.UPLOAD_IMAGE}
                                <input
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.webp"
                                    style={{ display: 'none' }}
                                    onChange={validateImage}
                                />
                            </label>
                        </div>
                    )}

                    {emptyLastFaq && lastIndex && (
                        <div className="text-danger">
                            {Language.BOTH_VALUES_REQUIRED}
                        </div>
                    )}

                    {length != 1 && (
                        <div>
                            <CButton
                                color="danger"
                                size="sm"
                                onClick={() => onRemoveFaq(id)}
                            >
                                {Language.REMOVE_FAQ}
                            </CButton>
                        </div>
                    )}

                    {faqIdWithNote === id && (
                        <div className="alert alert-danger">
                            {Language.UPLOAD_FAQ_NOTE}
                        </div>
                    )}
                </div>
            </CCol>
        </CRow>
    );
};

export default FAQItem;
